import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { ChromePicker } from 'react-color';
import { Input, Label } from 'reactstrap';
import { useIntl } from 'react-intl';

const HEX_PATTERN = /^#[0-9a-fA-F]{0,6}$/;

const normalizeHex = (value, fallback = '#222222') => {
  if (typeof value !== 'string' || !value.trim()) return fallback;
  const withHash = value.startsWith('#') ? value : `#${value}`;
  return withHash;
};

const ColorPickerField = ({
  value,
  onChange,
  label,
  showLabel,
  className,
  controlsClassName,
  buttonClassName,
  inputClassName,
  popoverClassName,
  defaultColor,
}) => {
  const intl = useIntl();
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef(null);

  const colorValue = useMemo(() => normalizeHex(value, defaultColor), [value, defaultColor]);

  useEffect(() => {
    const handleClick = (e) => {
      if (!pickerOpen) return;
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setPickerOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, [pickerOpen]);

  return (
    <div className={className}>
      {showLabel && (
        <Label className="puck-color-picker-field-label">
          {label}
        </Label>
      )}
      <div className={controlsClassName}>
        <button
          type="button"
          aria-label={intl.formatMessage({ defaultMessage: 'Pick color' })}
          className={buttonClassName}
          style={{ '--swatch-color': colorValue, backgroundColor: colorValue }}
          onClick={() => setPickerOpen((open) => !open)}
        />
        <Input
          type="text"
          value={colorValue}
          onChange={(e) => {
            let nextValue = e.target.value;
            if (!nextValue.startsWith('#')) nextValue = `#${nextValue}`;
            if (HEX_PATTERN.test(nextValue)) onChange(nextValue);
          }}
          maxLength={7}
          className={inputClassName}
          spellCheck={false}
          bsSize="sm"
        />
      </div>
      {pickerOpen && (
        <div ref={pickerRef} className={popoverClassName}>
          <ChromePicker
            color={colorValue}
            onChange={c => onChange(c.hex)}
            disableAlpha
          />
        </div>
      )}
    </div>
  );
};

ColorPickerField.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.node,
  showLabel: PropTypes.bool,
  className: PropTypes.string,
  controlsClassName: PropTypes.string,
  buttonClassName: PropTypes.string,
  inputClassName: PropTypes.string,
  popoverClassName: PropTypes.string,
  defaultColor: PropTypes.string,
};

ColorPickerField.defaultProps = {
  value: '#222222',
  label: null,
  showLabel: true,
  className: 'puck-color-picker-field-row',
  controlsClassName: 'puck-color-picker-field-controls',
  buttonClassName: 'puck-color-picker-field-btn',
  inputClassName: 'puck-color-picker-field-input',
  popoverClassName: 'puck-color-picker-field-popover',
  defaultColor: '#222222',
};

export default ColorPickerField;
